import moment from 'moment'
import { formatDate, parseDate, toIsoDate } from './dateFormat'
import { isRecord, responseErrorState } from './entityResponse'

export const DOCUMENT_TYPE_OPTIONS = Object.freeze([
  { value: 'all', label: 'Все типы' },
  { value: 'upd', label: 'УПД' },
  { value: 'avance', label: 'Счет-фактура на аванс' },
  { value: 'payment', label: 'Платежное поручение' },
  { value: 'bill_to_pay', label: 'Счет на оплату' },
  { value: 'act_sver', label: 'Акт сверки' },
  { value: 'accept_factura_avance', label: 'Зачет аванса' }
])

export const DOCUMENT_DIRECTION_OPTIONS = Object.freeze([
  { value: 'all', label: 'Любое направление' },
  { value: 'in', label: 'Входящие' },
  { value: 'out', label: 'Исходящие' },
  { value: 'internal', label: 'Внутренние' }
])

export const DOCUMENT_STATUS_OPTIONS = Object.freeze([
  { value: 'active', label: 'Активные' },
  { value: 'deleted', label: 'Удаленные' }
])

export const DOCUMENT_CREATE_OPTIONS = Object.freeze([
  { key: 'upd', label: 'УПД', routeName: 'document_create_upd' },
  { key: 'upd-simple', label: 'УПД по шагам', routeName: 'document_create_upd_simple' },
  { key: 'payment', label: 'Платежное поручение', routeName: 'document_create_payment' },
  { key: 'avance', label: 'Счет-фактура на аванс', routeName: 'document_create_factura_avance' },
  { key: 'bill-to-pay', label: 'Счет на оплату', routeName: 'document_create_bill_to_pay' },
  { key: 'act-sver', label: 'Акт сверки', routeName: 'document_create_act_sver' }
])

const DOCUMENT_INFO_ROUTES = Object.freeze({
  upd: 'document_info_upd',
  sale: 'document_info_upd',
  purchase: 'document_info_upd',
  payment: 'document_info_payment',
  avance: 'document_info_factura_avance',
  factura_avance: 'document_info_factura_avance',
  accept_factura_avance: 'document_info_factura_avance',
  bill_to_pay: 'document_info_bill_to_pay',
  act_sver: 'document_info_act_sver'
})

const LEGACY_DIRECTIONS = Object.freeze({ incoming: 'in', outgoing: 'out', insight: 'internal' })

function validIsoDate(value, fallback) {
  const parsed = parseDate(value)
  return parsed.isValid() ? toIsoDate(parsed) : fallback
}

export function defaultDocumentPeriod(storePeriod) {
  const fallback = {
    from: moment().startOf('quarter').format('YYYY-MM-DD'),
    to: moment().format('YYYY-MM-DD')
  }
  return {
    from: validIsoDate(storePeriod && storePeriod.from, fallback.from),
    to: validIsoDate(storePeriod && storePeriod.to, fallback.to)
  }
}

export function documentTypeLabel(type) {
  const option = DOCUMENT_TYPE_OPTIONS.find((item) => item.value === type)
  return option ? option.label : `Неподдерживаемый тип: ${type || 'не указан'}`
}

export function directionLabel(direction) {
  const option = DOCUMENT_DIRECTION_OPTIONS.find((item) => item.value === direction)
  return option ? option.label : direction || 'Не указано'
}

export function normalizeDocumentRegistryFilters(query = {}, storePeriod = {}, fixedType = null) {
  const defaults = defaultDocumentPeriod(storePeriod)
  const directionCandidate = LEGACY_DIRECTIONS[query.direction] || query.direction
  const direction = DOCUMENT_DIRECTION_OPTIONS.some((item) => item.value === directionCandidate)
    ? directionCandidate
    : 'all'
  const typeCandidate = fixedType || query.type || 'all'
  const type = DOCUMENT_TYPE_OPTIONS.some((item) => item.value === typeCandidate) ? typeCandidate : 'all'
  return {
    search: String(query.search || ''),
    direction,
    type,
    status: query.status === 'deleted' ? 'deleted' : 'active',
    period: {
      from: validIsoDate(query.from, defaults.from),
      to: validIsoDate(query.to, defaults.to)
    },
    vat: String(query.vat || ''),
    account: String(query.account || '')
  }
}

export function documentListType(filters, fixedType = null) {
  const type = fixedType || filters.type
  return !type || type === 'all' ? null : type
}

export function documentInfoRoute(row, paymentContext = '') {
  if (!row || row.id === undefined || row.id === null) return null
  const name = DOCUMENT_INFO_ROUTES[row.document_type]
  if (!name) return null
  return {
    name,
    params: { id: row.id, history_id: 'last' },
    ...(row.document_type === 'payment' && paymentContext ? { query: { context: paymentContext } } : {})
  }
}

function normalizeVatPeriod(value) {
  if (isRecord(value)) {
    return {
      value: value.value || '',
      label: value.label || 'Не установлен'
    }
  }
  return { value: value || '', label: value || 'Не установлен' }
}

export function normalizeDocumentList(response) {
  if (!isRecord(response) || response.type === 'error' || response.error) {
    return { ok: false, ...responseErrorState(response, 'Не удалось загрузить документы') }
  }
  if (!Array.isArray(response.data)) {
    return { ok: false, state: 'error', message: 'Сервер вернул некорректный список документов' }
  }
  return {
    ok: true,
    rows: response.data.filter(isRecord).map((row, index) => ({
      ...row,
      id: row.id ?? `document-${index}`,
      in_org_number: row.in_org_number ?? '—',
      name: String(row.name || `Документ №${row.in_org_number || '—'}`),
      date_display: formatDate(row.date, row.date || '—'),
      type_display: documentTypeLabel(row.document_type),
      direction_display: row.direction_display || directionLabel(row.direction),
      user_display: String(row.user || row.user_created?.display_name || row.user_created?.name || '—'),
      amount: row.summ_total ?? row.summ ?? null,
      nds_period_normalized: normalizeVatPeriod(row.nds_period),
      is_active: row.is_active !== false
    }))
  }
}

export function documentFilterQuery(filters, storePeriod, fixedType = null, isPaymentRegistry = false) {
  const defaults = defaultDocumentPeriod(storePeriod)
  return {
    ...(filters.search ? { search: filters.search } : {}),
    ...(filters.direction !== 'all' ? { direction: filters.direction } : {}),
    ...(!fixedType && filters.type !== 'all' ? { type: filters.type } : {}),
    ...(filters.status === 'deleted' ? { status: 'deleted' } : {}),
    ...(filters.period.from !== defaults.from ? { from: filters.period.from } : {}),
    ...(filters.period.to !== defaults.to ? { to: filters.period.to } : {}),
    ...(filters.vat ? { vat: filters.vat } : {}),
    ...(isPaymentRegistry && filters.account ? { account: filters.account } : {})
  }
}

export function documentServerFilterSignature(filters, fixedType = null, isPaymentRegistry = false) {
  return JSON.stringify({
    direction: filters.direction,
    document_type: documentListType(filters, fixedType),
    is_active: filters.status !== 'deleted',
    period: filters.period,
    account: isPaymentRegistry ? filters.account || null : null
  })
}
